import { AxiosError } from 'axios';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { draftRecognition, uploadFiles } from 'api/recognitions.service';
import { useToast } from 'hooks/ui';
import { Recognition } from 'models/recognition.model';
import { generateRecognitionRequestPayload } from 'hooks/helpers';
import { useApplicationStore } from 'store/application';
import { recognitionRoutes } from 'router/recognitionRoutes';
import { scrollToItem } from 'hooks/helpers/common';
import { recognitionKeys } from './queryKeys';

type HookArgs = {
  keepActive?: boolean;
};

export const useRecognitionCreateQuery = (
  props: HookArgs = { keepActive: false },
) => {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { setLoading, removeLoading } = useApplicationStore();

  const { isPending, mutate, data } = useMutation({
    mutationFn: async (recognition: Recognition) => {
      const newRecognition = generateRecognitionRequestPayload(recognition);

      setLoading();
      if (recognition.reward === 'PICTURE') {
        return draftRecognition(newRecognition).then(async (res) => {
          if (recognition.attachments?.length) {
            await uploadFiles(res.id, recognition.attachments);
          }

          return res;
        });
      }

      return draftRecognition(newRecognition);
    },
    onSuccess: (recognition) => {
      queryClient.invalidateQueries({ queryKey: recognitionKeys.all });
      toast.success('Recognition has been drafted successfully');

      if (props.keepActive) {
        navigate(recognitionRoutes.recognitionId(recognition.id));
        scrollToItem(recognition.id);
      }
    },
    onError: (error: AxiosError) => toast.error(error.message),
    onSettled: () => removeLoading(),
  });

  return { isLoading: isPending, mutate, data };
};
